import { createContext, useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import type { AccountDto } from '@/app/common/types/account.ts';
import type { UserRole } from '@/app/common/types/user.ts';
import { useLazyGetAccountQuery, useListAccountsQuery } from '@/app/store/api/accountApi.ts';
import { useUpdateUserMutation } from '@/app/store/api/userApi.ts';
import { useAuth } from '@/app/common/hooks/useAuth.ts';
import { setErrorContext } from '@/app/common/utils/errorReporter.ts';
import tracker from '@/app/common/utils/openreplay.ts';

export interface AccountContextValue {
  selectedAccount: AccountDto | null;
  selectedAccountId: number | null;
  isAdmin: boolean;
  isEditor: boolean;
  canWrite: boolean;
  loaded: boolean | null;
  noAccountAvailable: boolean;
  reload: () => Promise<void>;
  getAccountRole: (accountId?: number | null) => UserRole | null;
}

export const AccountContext = createContext<AccountContextValue>(null!);

export function AccountProvider({ children }: { children: ReactNode }) {
  const { isLoggedIn, user, logout, setUser } = useAuth();
  const [selectedAccount, setSelectedAccount] = useState<AccountDto | null>(null);
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isEditor, setIsEditor] = useState(false);
  const [canWrite, setCanWrite] = useState(false);
  const [loaded, setLoaded] = useState<boolean | null>(null);
  const [noAccountAvailable, setNoAccountAvailable] = useState(false);

  const { data: accountsResult } = useListAccountsQuery(undefined, { skip: !isLoggedIn });
  const accounts = accountsResult?.items;
  const [triggerGetAccount] = useLazyGetAccountQuery();
  const [updateUser] = useUpdateUserMutation();

  // Use refs for values accessed inside loadAccount to avoid unstable dependencies
  const userRef = useRef(user);
  userRef.current = user;
  const accountsRef = useRef(accounts);
  accountsRef.current = accounts;
  const selectedAccountIdRef = useRef(selectedAccountId);
  selectedAccountIdRef.current = selectedAccountId;

  const getAccountRole = useCallback((accountId?: number | null): UserRole | null => {
    if (!userRef.current) return null;
    if (userRef.current.isSuperAdmin) return 'Administrator';
    const aid = accountId ?? selectedAccountIdRef.current;
    const accountUser = userRef.current.accountUsers.find(au => au.accountId === aid);
    return accountUser?.accountRole ?? null;
  }, []);

  const loadAccount = useCallback(async () => {
    const currentUser = userRef.current;
    const currentAccounts = accountsRef.current;
    if (!currentUser || !currentAccounts) return;

    let accountId = currentUser.settings?.accountId ?? null;
    let account: AccountDto | null = null;

    // Validate that saved accountId is in the user's own account memberships
    // (not currentAccounts, which includes all accounts for superadmins).
    // Skip for superadmins — they can access any account but may not have
    // explicit AccountUser entries; triggerGetAccount handles authorization.
    if (accountId && !currentUser.isSuperAdmin && !currentUser.accountUsers?.some(au => au.accountId === accountId)) {
      accountId = null;
    }

    if (accountId) {
      try {
        account = await triggerGetAccount(accountId).unwrap();
      } catch {
        account = null;
      }
    }

    if (!accountId || !account) {
      if (currentAccounts.length === 0) {
        setNoAccountAvailable(true);
        setLoaded(false);
        logout();
        return;
      }
      accountId = currentAccounts[0].id;

      const updatedUser = { ...currentUser, settings: { ...currentUser.settings, accountId } };
      setUser(updatedUser);
      updateUser(updatedUser);

      try {
        account = await triggerGetAccount(accountId).unwrap();
      } catch {
        setNoAccountAvailable(true);
        setLoaded(false);
        logout();
        return;
      }
    }

    const role = getAccountRole(accountId);
    setSelectedAccount(account);
    setSelectedAccountId(accountId);
    setErrorContext({ accountId });
    tracker.setMetadata('accountId', String(accountId));
    setIsAdmin(role === 'Administrator' || currentUser.isSuperAdmin);
    setIsEditor(role === 'Administrator' || role === 'Editor' || currentUser.isSuperAdmin);
    setCanWrite(role === 'Administrator' || role === 'Editor' || currentUser.isSuperAdmin || currentUser.isDemoUser);
    setNoAccountAvailable(false);
    setLoaded(true);
  }, [triggerGetAccount, updateUser, getAccountRole, logout, setUser]);

  useEffect(() => {
    if (isLoggedIn && accounts) {
      loadAccount();
    } else if (isLoggedIn === false) {
      setSelectedAccount(null);
      setSelectedAccountId(null);
      setIsAdmin(false);
      setIsEditor(false);
      setCanWrite(false);
      setLoaded(null);
      setNoAccountAvailable(false);
    }
  }, [isLoggedIn, accounts, loadAccount]);

  const reload = useCallback(async () => {
    await loadAccount();
  }, [loadAccount]);

  return (
    <AccountContext.Provider value={{ selectedAccount, selectedAccountId, isAdmin, isEditor, canWrite, loaded, noAccountAvailable, reload, getAccountRole }}>
      {children}
    </AccountContext.Provider>
  );
}
